import { SetStateAction, useCallback, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import type { SurveyRow } from 'components/survey-description';
import { boundaryUserEditedAtom, surveyRowsAtom } from './atoms';
import { useUnitBuilder } from './context';

/**
 * Whether two row lists describe the same survey description.
 *
 * Compared by content rather than identity because a no-op edit must not cost an
 * undo step. Carbon's ComboBox reports a selection more than once — a clear
 * followed by the pick — so the row list is rewritten with the value it already
 * holds, and recording that left an undo entry that changed nothing visible when
 * popped. It read as "undo did nothing, press it again".
 *
 * @param {SurveyRow[]} a - One list.
 * @param {SurveyRow[]} b - The other.
 * @return {boolean} True when neither the rows nor their calls differ.
 */
const sameRows = (a: SurveyRow[], b: SurveyRow[]): boolean =>
  a.length === b.length &&
  a.every((row, index) => {
    const other = b[index];
    return (
      row.key === other.key &&
      row.location?.id === other.location?.id &&
      row.quarterCalls.length === other.quarterCalls.length &&
      row.quarterCalls.every((call, i) => call === other.quarterCalls[i])
    );
  });

/**
 * Writes survey rows as an undoable edit.
 *
 * The boundary is composed from the rows, so changing one is a boundary change —
 * a single action that undo has to reverse in both halves. The snapshot has to be
 * taken *here*, before the rows are written: the recomposed boundary only arrives
 * after a server round-trip, by which point the rows have already changed and a
 * snapshot taken then records the post-edit list. That is what left a removed row
 * missing after an undo while the shape came back correctly.
 *
 * Use for operator edits only. Seeding a unit's rows, hydrating tract geometry and
 * restoring a clone are not actions anyone asked to undo.
 *
 * @return {Function} A rows setter that records an undo entry first.
 */
export const useSurveyRowEdit = () => {
  const { shapeEdit } = useUnitBuilder();
  const rows = useAtomValue(surveyRowsAtom);
  const setRows = useSetAtom(surveyRowsAtom);
  // Changing the rows re-composes the boundary, so the freehand override has to
  // go — the mock clears `freehandPolys` in every one of its row mutators, and a
  // stale override would mask the change. Note the consequence, which is the
  // mock's too: adding or removing a survey area DISCARDS a vertex reshape.
  const setBoundaryUserEdited = useSetAtom(boundaryUserEditedAtom);

  // Everything this callback needs is read through a ref so the callback itself
  // keeps ONE identity for the life of the panel.
  //
  // That is a contract, not a preference: `useSurveyAreaPick` builds the map's
  // click handler from this setter and says so -- "the handler is registered with
  // the map, so it has to keep one identity ... it reads the current rows through
  // a ref rather than closing over them". Listing `rows` as a dependency gave this
  // a new identity after every edit, which rippled through `addById` and `pick`
  // and made `map-pick-control` detach and re-attach its click handler each time.
  // Picking one survey area then stopped responding until the toolbar action was
  // switched off and on -- that toggle was the operator forcing the re-attach.
  //
  // Reading through the ref also keeps functional updates honest: resolving
  // `setRows(current => ...)` against a value captured at render defeats the point
  // of passing a function.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const shapeEditRef = useRef(shapeEdit);
  shapeEditRef.current = shapeEdit;

  return useCallback(
    (next: SetStateAction<SurveyRow[]>) => {
      const current = rowsRef.current;
      const resolved = typeof next === 'function' ? next(current) : next;

      // A rewrite that changes nothing is not an edit: no entry, no write.
      if (sameRows(current, resolved)) return;

      // Every real edit gets an entry, so each one reverses on its own: filling
      // a row unfills it, and the row a preceding "Add survey area" created goes
      // on the next undo.
      //
      // Both halves are still pre-edit at this point, which is the whole point.
      shapeEditRef.current.pushHistory();
      setBoundaryUserEdited(false);
      setRows(resolved);

      // Removing the *last* tract needs no special case any more: the composition
      // is `baseRing ∪ tracts`, so with no tract left it is the base ring, and the
      // boundary (and the section line derived from it) reverts to the unit on its
      // own. It used to be handled here because the old effect only ever applied
      // `preview.geometry`, which goes null once the payload does.
    },
    [setRows, setBoundaryUserEdited]
  );
};
